import React, { useState } from "react";
import { 
  Card, 
  CardContent 
} from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, CheckCircle, XCircle, Trash, Users } from "lucide-react";
import { Lead } from "@/components/lead-card";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { deleteLead } from "@/services/leadsService";
import { useToast } from "@/hooks/use-toast";

// Type for our user data
interface UserData {
  id: string;
  name: string;
  email: string;
  role: string;
  leads: Lead[];
  position?: string;
}

interface UserLeadsSummaryProps {
  users: UserData[];
  onLeadDelete?: (leadId: string) => void;
}

// Helper function to capitalize the first letter
const capitalizeFirstLetter = (string?: string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Define status colors (can be moved to a shared util if used elsewhere)
const statusColors: Record<Exclude<Lead["status"], "delete-data">, string> = {
  "new": "#3b82f6",        // Blue
  "follow-up": "#8b5cf6",  // Purple
  "scheduled": "#10b981",  // Emerald
  "in-progress": "#f97316", // Orange
  "not-reached": "#f59e0b",// Amber
  "won": "#22c55e",        // Green
  "lost": "#ef4444",        // Red
  "wrong-data": "#94a3b8", // Gray
  "old-contact": "#64748b", // Slate-600
};

export function UserLeadsSummary({ users, onLeadDelete }: UserLeadsSummaryProps) {
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const { toast } = useToast();

  const toggleUserExpand = (userId: string) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  // Calculate summary statistics for a user's leads
  const calculateLeadStats = (leads: Lead[]) => {
    const totalLeads = leads.length;
    const newLeads = leads.filter(lead => lead.status === "new").length;
    const notReachedLeads = leads.filter(lead => lead.status === "not-reached").length;
    const followUpLeads = leads.filter(lead => lead.status === "follow-up").length;
    const scheduledLeads = leads.filter(lead => lead.status === "scheduled").length;
    const inProgressLeads = leads.filter(lead => lead.status === "in-progress").length;
    const wonLeads = leads.filter(lead => lead.status === "won").length;
    const lostLeads = leads.filter(lead => lead.status === "lost").length;
    const wrongDataLeads = leads.filter(lead => lead.status === "wrong-data").length;
    const oldContactLeads = leads.filter(lead => lead.status === "old-contact").length;
    const deleteLeadsCount = leads.filter(lead => lead.status === "delete-data").length;
    
    const relevantLeadsForOverallConversion = leads.filter(lead => 
      lead.status !== "new" && lead.status !== "wrong-data" && 
      lead.status !== "delete-data" && lead.status !== "not-reached" &&
      lead.status !== "old-contact"
    );
    const overallConversionRate = relevantLeadsForOverallConversion.length > 0 
      ? Math.round((wonLeads / relevantLeadsForOverallConversion.length) * 100) : 0;

    // Percentages for the status bar graph (relative to total leads)
    const newPercentage = totalLeads > 0 ? Math.round((newLeads / totalLeads) * 100) : 0;
    const followUpPercentage = totalLeads > 0 ? Math.round((followUpLeads / totalLeads) * 100) : 0;
    const scheduledPercentage = totalLeads > 0 ? Math.round((scheduledLeads / totalLeads) * 100) : 0;
    const notReachedPercentage = totalLeads > 0 ? Math.round((notReachedLeads / totalLeads) * 100) : 0;
    const inProgressPercentage = totalLeads > 0 ? Math.round((inProgressLeads / totalLeads) * 100) : 0;
    const wonPercentage = totalLeads > 0 ? Math.round((wonLeads / totalLeads) * 100) : 0;
    const lostPercentage = totalLeads > 0 ? Math.round((lostLeads / totalLeads) * 100) : 0;
    const wrongDataPercentage = totalLeads > 0 ? Math.round((wrongDataLeads / totalLeads) * 100) : 0;
    const oldContactPercentage = totalLeads > 0 ? Math.round((oldContactLeads / totalLeads) * 100) : 0;
    const deletePercentage = totalLeads > 0 ? Math.round((deleteLeadsCount / totalLeads) * 100) : 0;

    return {
      total: totalLeads,
      new: newLeads,
      notReached: notReachedLeads,
      followUp: followUpLeads,
      scheduled: scheduledLeads,
      inProgress: inProgressLeads,
      won: wonLeads,
      lost: lostLeads,
      wrongData: wrongDataLeads,
      oldContact: oldContactLeads,
      deleteData: deleteLeadsCount,
      newPercentage,
      notReachedPercentage,
      followUpPercentage,
      scheduledPercentage,
      inProgressPercentage,
      wonPercentage,
      lostPercentage,
      wrongDataPercentage,
      oldContactPercentage,
      deletePercentage,
      overallConversionRate,
      totalClosed: wonLeads + lostLeads,
      wonPercentageOfClosed: (wonLeads + lostLeads) > 0 ? Math.round((wonLeads / (wonLeads + lostLeads)) * 100) : 0,
      lostPercentageOfClosed: (wonLeads + lostLeads) > 0 ? Math.round((lostLeads / (wonLeads + lostLeads)) * 100) : 0
    };
  };

  // Format date safely
  const safeFormatDate = (date: Date | string | undefined, formatStr: string): string => {
    if (!date) return "-";
    try {
      return format(new Date(date), formatStr, { locale: de });
    } catch (e) {
      return "-";
    }
  };

  // Handle lead deletion
  const handleDeleteLead = async (leadId: string) => {
    try {
      const success = await deleteLead(leadId);
      if (success) {
        toast({
          title: "Lead gelöscht",
          description: "Der Lead wurde erfolgreich gelöscht",
        });
        
        // Notify parent component about the deletion
        if (onLeadDelete) {
          onLeadDelete(leadId);
        }
      } else {
        toast({
          title: "Fehler",
          description: "Der Lead konnte nicht gelöscht werden",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast({
        title: "Fehler",
        description: "Der Lead konnte nicht gelöscht werden",
        variant: "destructive",
      });
    }
  };

  // Get leads marked for deletion for a user
  const getLeadsForDeletion = (leads: Lead[]) => {
    return leads.filter(lead => lead.status === "delete-data");
  };

  // For expanded view - status details
  const statusOrder: Exclude<Lead["status"], "delete-data">[] = ["new", "follow-up", "scheduled", "in-progress", "not-reached", "won", "lost", "wrong-data", "old-contact"];
  const statusLabels: Record<Exclude<Lead["status"], "delete-data">, string> = {
    "new": "Neu",
    "follow-up": "Wiedervorlage",
    "scheduled": "Terminiert",
    "in-progress": "In Prozess",
    "not-reached": "Nicht erreicht",
    "won": "Gewonnen",
    "lost": "Verloren",
    "wrong-data": "Falsche Daten",
    "old-contact": "Altkontakt",
  };

  return (
    <Card className="shadow-sm">
      <CardContent className="pt-6">
        <div className="overflow-x-auto">
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead className="py-2 px-1 w-10"></TableHead>
                <TableHead className="py-2 px-3 min-w-[120px] max-w-[180px]">Name</TableHead>
                <TableHead className="py-2 px-3 min-w-[120px] max-w-[180px]">E-Mail</TableHead>
                <TableHead className="py-2 px-3 min-w-[100px] max-w-[150px]">Position</TableHead>
                <TableHead className="py-2 px-3 text-right min-w-[70px]">Leads Ges.</TableHead>
                <TableHead className="py-2 px-3 text-right min-w-[70px]">Gewonnen</TableHead>
                <TableHead className="py-2 px-3 text-right min-w-[80px]">Konversion</TableHead>
                <TableHead className="py-2 px-3 text-right min-w-[80px]">Zu löschen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-10 text-xs">
                    Keine Benutzerdaten für den ausgewählten Zeitraum oder keine Benutzer vorhanden.
                  </TableCell>
                </TableRow>
              )}
              {users.map((user) => {
                const stats = calculateLeadStats(user.leads);
                const leadsForDeletion = getLeadsForDeletion(user.leads);
                const isExpanded = expandedUser === user.id;
                
                return (
                  <React.Fragment key={user.id}>
                    <TableRow className={cn("hover:bg-muted/50 cursor-pointer", isExpanded && "bg-muted/50")} onClick={() => toggleUserExpand(user.id)}>
                      <TableCell className="py-1.5 px-1 w-10">
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </Button>
                      </TableCell>
                      <TableCell className="py-1.5 px-3 font-medium break-words max-w-[180px]">{user.name}</TableCell>
                      <TableCell className="py-1.5 px-3 text-muted-foreground break-words max-w-[180px]">{user.email}</TableCell>
                      <TableCell className="py-1.5 px-3 text-muted-foreground break-words max-w-[150px]">
                        {capitalizeFirstLetter(user.position) || '-'}
                      </TableCell>
                      <TableCell className="py-1.5 px-3 text-right">{stats.total}</TableCell>
                      <TableCell className="py-1.5 px-3 text-right text-green-600 font-medium">{stats.won}</TableCell>
                      <TableCell className="py-1.5 px-3 text-right font-medium">{stats.overallConversionRate}%</TableCell>
                      <TableCell className="py-1.5 px-3 text-right">
                        {stats.deleteData > 0 ? (
                          <span className="text-red-500 font-semibold">{stats.deleteData}</span>
                        ) : (
                          <span>0</span>
                        )}
                      </TableCell>
                    </TableRow>
                    
                    {isExpanded && (
                      <TableRow className="bg-white dark:bg-background">
                        <TableCell colSpan={8} className="p-0">
                          <div className="p-4 space-y-6">
                            <div className="border-b pb-4">
                                <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">Lead-Status Details:</h4>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
                                  {statusOrder.map(statusKey => {
                                    let count = 0;
                                    switch (statusKey) {
                                      case 'new': count = stats.new; break;
                                      case 'follow-up': count = stats.followUp; break;
                                      case 'scheduled': count = stats.scheduled; break;
                                      case 'in-progress': count = stats.inProgress; break;
                                      case 'not-reached': count = stats.notReached; break;
                                      case 'won': count = stats.won; break;
                                      case 'lost': count = stats.lost; break;
                                      case 'wrong-data': count = stats.wrongData; break;
                                      case 'old-contact': count = stats.oldContact; break;
                                    }
                                    return (
                                      <div key={statusKey} className="flex justify-between items-center">
                                        <span className="text-muted-foreground">
                                          <span 
                                            className="inline-block w-2.5 h-2.5 rounded-full mr-2"
                                            style={{ backgroundColor: statusColors[statusKey] }}
                                          ></span>
                                          {statusLabels[statusKey]}:
                                        </span>
                                        <span className="font-medium">{count}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                            </div>

                            <div className="border-b pb-4">
                                <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">Grafische Statusübersicht (von {stats.total} Leads):</h4>
                                {stats.total > 0 ? (
                                  <div className="mt-1 w-full h-6 bg-gray-200 dark:bg-gray-700 rounded-md overflow-hidden flex">
                                    {statusOrder.map(statusKey => {
                                      let percentage = 0;
                                      let count = 0;
                                      switch (statusKey) {
                                        case 'new': percentage = stats.newPercentage; count = stats.new; break;
                                        case 'follow-up': percentage = stats.followUpPercentage; count = stats.followUp; break;
                                        case 'scheduled': percentage = stats.scheduledPercentage; count = stats.scheduled; break;
                                        case 'in-progress': percentage = stats.inProgressPercentage; count = stats.inProgress; break;
                                        case 'not-reached': percentage = stats.notReachedPercentage; count = stats.notReached; break;
                                        case 'won': percentage = stats.wonPercentage; count = stats.won; break;
                                        case 'lost': percentage = stats.lostPercentage; count = stats.lost; break;
                                        case 'wrong-data': percentage = stats.wrongDataPercentage; count = stats.wrongData; break;
                                        case 'old-contact': percentage = stats.oldContactPercentage; count = stats.oldContact; break;
                                      }
                                      if (percentage === 0) return null; // Don't render a div if percentage is 0
                                      return (
                                        <div 
                                          key={statusKey}
                                          className="h-full transition-all duration-300 ease-in-out flex items-center justify-center"
                                          style={{ 
                                            width: `${percentage}%`, 
                                            backgroundColor: statusColors[statusKey]
                                          }}
                                          title={`${statusLabels[statusKey]}: ${count} (${percentage}%)`}
                                        >
                                          {/* Optional: show percentage if it's large enough */}
                                          {/* {percentage > 10 && <span className="text-white text-[10px] font-medium">{percentage}%</span>} */}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className="text-xs text-muted-foreground italic">Keine Leads für grafische Übersicht vorhanden.</p>
                                )}
                            </div>

                            <div className="border-b pb-4">
                                <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">Analyse abgeschlossener Leads ({stats.totalClosed} gesamt):</h4>
                                {stats.totalClosed > 0 ? (
                                  <div className="space-y-2 text-xs">
                                    <div className="flex justify-between items-center">
                                      <span className="text-muted-foreground">Gewonnen (von Geschlossenen):</span>
                                      <span className="font-medium text-green-600">{stats.won} ({stats.wonPercentageOfClosed}%)</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span className="text-muted-foreground">Verloren (von Geschlossenen):</span>
                                      <span className="font-medium text-red-600">{stats.lost} ({stats.lostPercentageOfClosed}%)</span>
                                    </div>
                                    <div className="mt-2 w-full h-5 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden flex" title={`Gewonnen: ${stats.won} (${stats.wonPercentageOfClosed}%), Verloren: ${stats.lost} (${stats.lostPercentageOfClosed}%)`}>
                                      <div className="h-full bg-green-500 transition-all duration-300 ease-in-out" style={{ width: `${stats.wonPercentageOfClosed}%` }}></div>
                                      <div className="h-full bg-red-500 transition-all duration-300 ease-in-out" style={{ width: `${stats.lostPercentageOfClosed}%` }}></div>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-xs text-muted-foreground italic">Keine abgeschlossenen Leads für diese Analyse vorhanden.</p>
                                )}
                            </div>

                            <div className="border-b pb-4">
                                <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">Weitere Metriken:</h4>
                                <div className="text-xs text-muted-foreground italic">
                                  <p>- Durchschnittliche Bearbeitungszeit (benötigt zusätzliche Daten)</p>
                                  <p>- Lead-Quelle Analyse (benötigt zusätzliche Daten)</p>
                                  {/* Add other placeholder metrics as needed */}
                                </div>
                            </div>

                            {leadsForDeletion.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-red-700 mb-2 uppercase tracking-wider">
                                  Zu löschende Kontakte ({leadsForDeletion.length}):
                                </h4>
                                <div className="space-y-1.5 max-h-60 overflow-y-auto pr-2">
                                  {leadsForDeletion.map((lead) => (
                                    <div 
                                      key={lead.id} 
                                      className="bg-muted/30 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs shadow-sm"
                                    >
                                      <div className="flex flex-col">
                                        <span className="font-medium truncate max-w-[250px]">
                                          {`${lead.firstName} ${lead.lastName}`}
                                        </span>
                                        <span className="text-muted-foreground truncate max-w-[250px]">
                                          {lead.email || "(Keine E-Mail)"} ({lead.status})
                                        </span>
                                      </div>
                                      <Button 
                                        variant="destructive" 
                                        size="sm"
                                        className="h-7 py-0 px-2.5 text-xs whitespace-nowrap"
                                        onClick={() => handleDeleteLead(lead.id)}
                                      >
                                        <Trash className="h-3 w-3 mr-1.5" />
                                        Löschen
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
